/**
 * ProductionMonitoringPage
 * 
 * Main page component for Production Budget Monitoring module
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    VStack,
    Heading,
    Text,
    HStack,
    useToast,
} from '@chakra-ui/react';
import Navbar from '../../../../components/Navbar';
import { useProductionMonitoring } from '../hooks/useProductionMonitoring';
import FilterProductionMonitoring from '../components/FilterProductionMonitoring';
import ProductionMonitoringTable from '../components/ProductionMonitoringTable';
import ProductionMonitoringApiService from '../services/productionMonitoringApiService';
import { ProductionBudgetFilters } from '../types';

/**
 * Main Production Monitoring Page Component
 */
const ProductionMonitoringPage: React.FC = () => {
    const toast = useToast();

    const {
        displayRows,
        loading,
        filters,
        setFilters,
        fetchData,
    } = useProductionMonitoring();

    const [isExporting, setIsExporting] = useState(false);

    /**
     * Fetch data on initial load
     */
    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Handle filter changes
     */
    const handleFilterChange = useCallback(
        (newFilters: ProductionBudgetFilters) => {
            setFilters(newFilters);
        },
        [setFilters]
    );

    /**
     * Handle clear filters
     */
    const handleClearFilters = useCallback(() => {
        toast({
            title: 'Filter direset',
            description: 'Semua filter telah dikembalikan ke nilai default',
            status: 'info',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
        });
    }, [toast]);

    /**
     * Handle apply filters - accepts filters as parameter to avoid stale state
     */
    const handleApplyFilters = useCallback((filtersToApply: ProductionBudgetFilters) => {
        setFilters(filtersToApply);
        // Fetch data with the new filters immediately
        fetchData(filtersToApply);
        toast({
            title: 'Filter diterapkan',
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top-right',
        });
    }, [setFilters, fetchData, toast]);

    /**
     * Handle export to Excel via Backend Streaming
     */
    const handleExport = useCallback(async () => {
        if (!displayRows || displayRows.length === 0) {
            toast({
                title: 'Tidak ada data',
                description: 'Tidak ada data untuk diekspor',
                status: 'warning',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            });
            return;
        }

        try {
            setIsExporting(true);
            toast({
                title: 'Mengekspor data',
                description: 'Sedang membuat file Excel (Summary & Detail Per Blok)...',
                status: 'info',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            });

            await ProductionMonitoringApiService.downloadProductionBudgetExcel(filters);

            toast({
                title: 'Berhasil',
                description: 'Data berhasil diekspor ke Excel',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: 'top-right',
            });
        } catch (error: any) {
            toast({
                title: 'Gagal ekspor',
                description: error.message || 'Terjadi kesalahan saat mengekspor data',
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top-right',
            });
        } finally {
            setIsExporting(false);
        }
    }, [displayRows, filters, toast]);

    /**
     * Handle refresh data
     */
    const handleRefresh = useCallback(() => {
        fetchData();
    }, [fetchData]);

    return (
        <Box minH="100vh" bg="gray.50">
            <Navbar />
            
            <Box flex="1" p={{ base: 4, md: 6 }}>
                <VStack spacing={6} align="stretch">
                    {/* Page Header */}
                    <VStack align="start" spacing={1}>
                        <Heading size="lg" color="customGreen.500">
                            Production Budget Monitoring
                        </Heading>
                        <HStack spacing={4} flexWrap="wrap">
                            <Text color="gray.600" fontSize="sm">
                                Monitoring penggunaan budget produksi TBS
                            </Text>
                        </HStack>
                    </VStack>

                    {/* Filter Section */}
                    <FilterProductionMonitoring
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onClearFilters={handleClearFilters}
                        onApplyFilters={handleApplyFilters}
                        onExport={handleExport}
                        onRefresh={handleRefresh}
                        isExporting={isExporting}
                    />

                    {/* Data Count */}
                    {!loading && displayRows.length > 0 && (
                        <HStack justify="space-between" px={2}>
                            <Text fontSize="sm" color="gray.600">
                                Menampilkan <strong>{displayRows.length}</strong> baris data
                            </Text>
                        </HStack>
                    )}

                    {/* Table Section */}
                    <ProductionMonitoringTable
                        data={displayRows}
                        loading={loading}
                    />
                </VStack>
            </Box>
        </Box>
    );
};

export default ProductionMonitoringPage;
